import type { ConversationItemAvatarProps } from './index.types';
import ActivityItemPresenceDisplay from 'Components/ActivityItemPresenceDisplay';
import { AxiosResponseT, ResultsCollection } from 'Interfaces/axiosResponse';
import { isEmpty } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import { IPromiseBasedObservable } from 'mobx-utils';
import { ContactModelV2 } from 'Models/ContactModelV2';
import ParticipantModel from 'Models/ParticipantModel';
import * as React from 'react';
import { Message } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberNoPlusIfUS } from 'Utils/phoneUtil';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { cn } from '~/lib/utils';

// Modified copy of src/app/components/ActivityListItem/index.tsx's use of ActivityItemPresenceDisplayObserver
const ConversationItemAvatar: React.FC<ConversationItemAvatarProps> = ({
  conversation,
}) => {
  const { person: personStore, participant: participantStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  const participantsPbo =
    participantStore.loadConversationParticipantsIfMissing(conversation.id);

  let personPbo: IPromiseBasedObservable<ContactModelV2> = null;

  return (
    <div className={cn('shrink-0')}>
      {participantsPbo.case({
        pending: () => null,
        rejected: (reason) =>
          reason?.message === 'Network Error' ? null : (
            <Message
              visible
              error
              content={
                'Error loading Participant: ' +
                (reason?.response?.data?.message ?? 'No Message')
              }
            />
          ),
        fulfilled: (
          ptcResp: AxiosResponseT<ResultsCollection<ParticipantModel>>
        ) => {
          const ptcDataResults: ParticipantModel[] = ptcResp.data.results ?? [];

          const otherPerson = ptcDataResults.find(
            (participant) =>
              participant.personId &&
              participant.personId !== personStore.loggedInPersonId
          );
          const otherPersonPhoneNumber = ptcDataResults.find(
            (participant) => !isNullOrUndefined(participant.phone)
          );
          const mySelf = ptcDataResults.find(
            (participant) =>
              participant.personId === personStore.loggedInPersonId
          );
          const phoneNumNoPlus = isEmpty(otherPersonPhoneNumber?.phone)
            ? null
            : formatNumberNoPlusIfUS(otherPersonPhoneNumber.phone);

          if (
            !isNullOrUndefined(otherPerson) &&
            isNullOrUndefined(otherPersonPhoneNumber)
          ) {
            const opId = otherPerson.personId ?? 0;
            personPbo = personStore.loadPersonByIdGetIfMissingGet(
              opId.toString()
            );
            return (
              <ActivityItemPresenceDisplay
                conversationGrouping={conversation.grouping}
                conversationId={conversation?.id}
                personId={opId.toString()}
                phoneNumber={phoneNumNoPlus}
                personPbo={personPbo}
                showUnread={true}
              />
            );
          } else if (
            mySelf !== undefined &&
            otherPerson === undefined &&
            isNullOrUndefined(otherPersonPhoneNumber)
          ) {
            // nobody but yourself (so lonely)
            personPbo = personStore.loadPersonByIdGetIfMissingGet(
              mySelf.personId.toString()
            );
            return (
              <ActivityItemPresenceDisplay
                conversationGrouping={conversation.grouping}
                conversationId={conversation?.id}
                personId={personStore.loggedInPersonId.toString()}
                personPbo={personPbo}
                phoneNumber={phoneNumNoPlus}
                showUnread={true}
              />
            );
          } else {
            // external/unknown users
            return (
              <ActivityItemPresenceDisplay
                conversationGrouping={conversation.grouping}
                conversationId={conversation?.id}
                phoneNumber={phoneNumNoPlus}
                personId={(otherPerson?.personId ?? 0)?.toString()}
                personPbo={personPbo}
                showUnread={true}
              />
            );
          }
        },
      })}
    </div>
  );
};

export default observer(ConversationItemAvatar);
